import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Badge, Button, Separator } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { History } from 'lucide-react';
import { toast } from 'sonner';
import { useReviewNoticePublication } from '../hooks/useNoticeCoverage';
import { useNoticePublications } from '../hooks/useNoticePublications';
const REVIEW_LABELS = {
    pending_review: 'Pending review',
    reviewed: 'Reviewed'
};
/**
 * Notice detail section: the original publication and every amendment issued
 * against it, so an amendment is visible before anyone has reviewed it and the
 * original stays on record (SAD-466). Renders nothing for a Notice with no
 * recorded lineage.
 */ export function NoticePublications({ noticeId }) {
    const { data: publications = [] } = useNoticePublications(noticeId);
    const review = useReviewNoticePublication(noticeId);
    if (publications.length === 0) return null;
    const current = Math.max(...publications.map((p)=>p.version));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Separator, {}),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.noticeAmendment.section,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 text-sm text-muted-foreground mb-2",
                        children: [
                            /*#__PURE__*/ _jsx(History, {
                                className: "h-4 w-4"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: "Publication history"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "ml-6 space-y-2",
                        children: publications.map((p)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between gap-2 text-sm",
                                "data-testid": Testids.noticeAmendment.item,
                                "data-notice-id": p.noticeId,
                                "data-version": p.version,
                                "data-review-status": p.reviewStatus,
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            "v",
                                            p.version,
                                            " · ",
                                            p.publicationType === 'original' ? 'Original' : 'Amendment',
                                            p.source ? ` · ${p.source}` : '',
                                            p.sourceIdentifier ? ` ${p.sourceIdentifier}` : '',
                                            p.version === current ? ' · current' : ''
                                        ]
                                    }),
                                    p.origin === 'manual' && /*#__PURE__*/ _jsxs("span", {
                                        className: "text-xs",
                                        "data-testid": Testids.noticeAmendment.origin,
                                        children: [
                                            "Manual — ",
                                            p.provenance
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            p.reviewStatus === 'reviewed' && /*#__PURE__*/ _jsxs("span", {
                                                className: "text-xs text-muted-foreground",
                                                "data-testid": Testids.noticeAmendment.reviewedLabel,
                                                children: [
                                                    "Reviewed by ",
                                                    p.reviewedBy,
                                                    p.reviewedAt ? ` at ${p.reviewedAt.toDate().toLocaleString()}` : ''
                                                ]
                                            }),
                                            REVIEW_LABELS[p.reviewStatus] && /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                "data-testid": Testids.noticeAmendment.reviewStatus,
                                                children: REVIEW_LABELS[p.reviewStatus]
                                            }),
                                            p.reviewStatus === 'pending_review' && /*#__PURE__*/ _jsx(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                disabled: review.isPending,
                                                "data-testid": Testids.noticeAmendment.reviewButton,
                                                onClick: ()=>review.mutate(p.id, {
                                                        onError: ()=>toast.error('Could not mark reviewed')
                                                    }),
                                                children: "Mark reviewed"
                                            })
                                        ]
                                    })
                                ]
                            }, p.id))
                    })
                ]
            })
        ]
    });
}
